import { useEffect, useRef } from "react";
import { isTauri } from "@tauri-apps/api/core";
import { Menu, MenuItem, PredefinedMenuItem, Submenu } from "@tauri-apps/api/menu";
import { getCurrent as getCurrentDeepLinks, onOpenUrl } from "@tauri-apps/plugin-deep-link";
import { openUrl } from "@tauri-apps/plugin-opener";
import { useSettings } from "@/contexts/SettingsContext";
import { getDesktopDocument, subscribeDesktopDocument } from "@/lib/desktop-session";
import { getRecentDocuments, subscribeRecentDocuments } from "@/lib/desktop-recent";
import { hideMainWindow, revealMainWindow } from "@/lib/desktop-window";
import { isPresenterSurface } from "@/lib/desktop-window";
import { drainOutboxNow } from "@/lib/desktop-sync";
import { isDesktopApp } from "@/lib/runtime";
import { APP_EVENTS, APP_NAME } from "@/lib/brand";

declare global {
  interface Window {
    __sermonSpaceDesktopMenu?: boolean;
  }
}

function navigate(path: string) {
  void revealMainWindow();
  if (window.location.pathname === path) return;
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Take a `sermonspace://` URL to its screen.
 *
 * The scheme is the door the rest of the OS comes through — Spotlight results,
 * Shortcuts, links in email from the web app. Unrecognised hosts are ignored
 * rather than guessed at, so a newer link into an older build does nothing
 * instead of something surprising.
 *
 *   sermonspace://document/42        → edit document 42
 *   sermonspace://document/42/read   → reading view
 *   sermonspace://search?q=grace     → search results
 *   sermonspace://new                → new document
 *   sermonspace://library | notes | quotes  → those sections
 */
function handleDeepLink(raw: string) {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return;
  }
  if (url.protocol !== "sermonspace:") return;

  const host = url.hostname || url.pathname.split("/").filter(Boolean)[0] || "";
  const parts = url.pathname.split("/").filter(Boolean);

  if (host === "document" && /^\d+$/.test(parts[0] ?? "")) {
    navigate(`/documents/${parts[0]}${parts[1] === "read" ? "" : "/edit"}`);
    return;
  }
  if (host === "search") {
    const q = url.searchParams.get("q")?.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    return;
  }
  if (host === "new") {
    navigate("/documents/create");
    return;
  }
  if (host === "library" || host === "notes" || host === "quotes") {
    navigate(`/${host}`);
  }
}

const LAST_PATH_KEY = "sermonspace:last-path";

/** Paths that make no sense to reopen into. */
function isRestorablePath(path: string): boolean {
  return !path.startsWith("/auth") && path !== "/";
}

function closeDocumentOrWindow() {
  const session = getDesktopDocument();
  if (session) {
    session.close();
    return;
  }
  const path = window.location.pathname;
  if (path.startsWith("/documents/") || path.startsWith("/notes/") || path.startsWith("/quotes/")) {
    navigate("/");
    return;
  }
  void hideMainWindow();
}

/**
 * Adds macOS behavior without making the browser build depend on it.
 *
 * The application menu is deliberately native, not an HTML imitation: this is
 * where muscle-memory commands live on a Mac, and it remains available while
 * a modal, editor, or reading view has focus.
 */
export function DesktopBridge() {
  const { openSettings } = useSettings();
  const openSettingsRef = useRef(openSettings);
  openSettingsRef.current = openSettings;

  useEffect(() => {
    if (!isTauri() || isPresenterSurface()) return;
    document.documentElement.dataset.runtime = "desktop";

    let menuKey = "";

    const installMenu = async () => {
      const session = getDesktopDocument();
      const recent = getRecentDocuments();
      const nextKey = `${session ? "doc" : "none"}:${session?.hasSlides ? "slides" : ""}:${recent.map((item) => item.id).join(",")}`;
      if (nextKey === menuKey && window.__sermonSpaceDesktopMenu) return;
      menuKey = nextKey;
      window.__sermonSpaceDesktopMenu = true;

      const aboutMenu = await Submenu.new({
        text: APP_NAME,
        items: [
          await PredefinedMenuItem.new({
            item: {
              About: {
                name: APP_NAME,
                version: "0.1.0",
                copyright: `© 2026 ${APP_NAME}`,
              },
            },
          }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await MenuItem.new({
            id: "settings",
            text: "Settings…",
            accelerator: "CmdOrCtrl+,",
            action: () => openSettingsRef.current(),
          }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "Services" }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "Hide" }),
          await PredefinedMenuItem.new({ item: "HideOthers" }),
          await PredefinedMenuItem.new({ item: "ShowAll" }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "Quit", text: `Quit ${APP_NAME}` }),
        ],
      });

      const recentItems =
        recent.length === 0
          ? [
              await MenuItem.new({
                id: "recent-empty",
                text: "No Recent Documents",
                enabled: false,
                action: () => undefined,
              }),
            ]
          : await Promise.all(
              recent.map((item) =>
                MenuItem.new({
                  id: `recent-${item.id}`,
                  text: item.title,
                  action: () => navigate(`/documents/${item.id}/edit`),
                }),
              ),
            );

      const exportItems = [
        await MenuItem.new({
          id: "export-md",
          text: "Manuscript Markdown…",
          enabled: Boolean(session),
          action: () => getDesktopDocument()?.exportKind("manuscript-md"),
        }),
        await MenuItem.new({
          id: "export-pdf",
          text: "PDF / Print…",
          enabled: Boolean(session),
          action: () => getDesktopDocument()?.exportKind("manuscript-print"),
        }),
        await MenuItem.new({
          id: "export-pptx",
          text: "PowerPoint…",
          enabled: Boolean(session?.hasSlides),
          action: () => getDesktopDocument()?.exportKind("pptx"),
        }),
        await MenuItem.new({
          id: "export-images",
          text: "Slide Images…",
          enabled: Boolean(session?.hasSlides),
          action: () => getDesktopDocument()?.exportKind("images"),
        }),
        await MenuItem.new({
          id: "export-slides-pdf",
          text: "Slides PDF…",
          enabled: Boolean(session?.hasSlides),
          action: () => getDesktopDocument()?.exportKind("slides-pdf"),
        }),
        await MenuItem.new({
          id: "export-marp",
          text: "Marp Markdown…",
          enabled: Boolean(session?.hasSlides),
          action: () => getDesktopDocument()?.exportKind("marp"),
        }),
      ];

      const fileMenu = await Submenu.new({
        text: "File",
        items: [
          await MenuItem.new({
            id: "new-document",
            text: "New Document",
            accelerator: "CmdOrCtrl+N",
            action: () => navigate("/documents/create"),
          }),
          await Submenu.new({ text: "Open Recent", items: recentItems }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await MenuItem.new({
            id: "save-document",
            text: "Save",
            accelerator: "CmdOrCtrl+S",
            enabled: Boolean(session),
            action: () => getDesktopDocument()?.save(),
          }),
          await MenuItem.new({
            id: "print-document",
            text: "Print…",
            accelerator: "CmdOrCtrl+P",
            enabled: Boolean(session),
            action: () => getDesktopDocument()?.print(),
          }),
          await Submenu.new({ text: "Export", items: exportItems }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await MenuItem.new({
            id: "close-document",
            text: "Close",
            accelerator: "CmdOrCtrl+W",
            action: closeDocumentOrWindow,
          }),
        ],
      });

      const editMenu = await Submenu.new({
        text: "Edit",
        items: [
          await PredefinedMenuItem.new({ item: "Undo" }),
          await PredefinedMenuItem.new({ item: "Redo" }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "Cut" }),
          await PredefinedMenuItem.new({ item: "Copy" }),
          await PredefinedMenuItem.new({ item: "Paste" }),
          await PredefinedMenuItem.new({ item: "SelectAll" }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await MenuItem.new({
            id: "find",
            text: "Find…",
            accelerator: "CmdOrCtrl+F",
            enabled: Boolean(session),
            action: () => {
              window.dispatchEvent(new CustomEvent(APP_EVENTS.openFind));
            },
          }),
        ],
      });

      const viewMenu = await Submenu.new({
        text: "View",
        items: [
          await MenuItem.new({
            id: "dashboard",
            text: "Dashboard",
            accelerator: "CmdOrCtrl+1",
            action: () => navigate("/"),
          }),
          await MenuItem.new({
            id: "library",
            text: "Library",
            accelerator: "CmdOrCtrl+2",
            action: () => navigate("/library"),
          }),
          await MenuItem.new({
            id: "notes",
            text: "Notes",
            accelerator: "CmdOrCtrl+3",
            action: () => navigate("/notes"),
          }),
          await MenuItem.new({
            id: "quotes",
            text: "Quotes",
            accelerator: "CmdOrCtrl+4",
            action: () => navigate("/quotes"),
          }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await MenuItem.new({
            id: "search",
            text: `Search ${APP_NAME}…`,
            accelerator: "CmdOrCtrl+K",
            action: () => {
              window.dispatchEvent(new CustomEvent(APP_EVENTS.openSearch));
            },
          }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "Fullscreen" }),
        ],
      });

      const windowMenu = await Submenu.new({
        text: "Window",
        items: [
          await PredefinedMenuItem.new({ item: "Minimize" }),
          await PredefinedMenuItem.new({ item: "Maximize" }),
          await PredefinedMenuItem.new({ item: "Separator" }),
          await PredefinedMenuItem.new({ item: "BringAllToFront" }),
        ],
      });

      const helpMenu = await Submenu.new({
        text: "Help",
        items: [
          await MenuItem.new({
            id: "help-site",
            text: `${APP_NAME} Help`,
            action: () => void openUrl("https://sermonspace.app"),
          }),
        ],
      });

      const menu = await Menu.new({
        items: [aboutMenu, fileMenu, editMenu, viewMenu, windowMenu, helpMenu],
      });
      await menu.setAsAppMenu();
      await windowMenu.setAsWindowsMenuForNSApp();
    };

    void installMenu().catch((error) => {
      window.__sermonSpaceDesktopMenu = false;
      menuKey = "";
      console.error(`Could not install the ${APP_NAME} desktop menu`, error);
    });

    const rebuild = () => {
      void installMenu().catch((error) => {
        console.error(`Could not refresh the ${APP_NAME} desktop menu`, error);
      });
    };

    const unsubSession = subscribeDesktopDocument(rebuild);
    const unsubRecent = subscribeRecentDocuments(rebuild);

    if (isDesktopApp) {
      void drainOutboxNow();
    }

    /* State restoration: reopen where the author left off. The window's size
     * and position are the window-state plugin's job; the route is ours. */
    const savedPath = localStorage.getItem(LAST_PATH_KEY);
    if (savedPath && isRestorablePath(savedPath) && window.location.pathname === "/") {
      window.history.replaceState(null, "", savedPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    const rememberPath = () => {
      const path = window.location.pathname + window.location.search;
      if (!path.startsWith("/auth")) localStorage.setItem(LAST_PATH_KEY, path);
    };
    // wouter patches history and announces both, so in-app navigation is heard.
    window.addEventListener("popstate", rememberPath);
    window.addEventListener("pushState", rememberPath);
    window.addEventListener("replaceState", rememberPath);

    /* Deep links: the cold-start URL (the app was opened by a link) and any
     * link clicked while the app is already running. */
    void getCurrentDeepLinks()
      .then((urls) => {
        for (const url of urls ?? []) handleDeepLink(url);
      })
      .catch(() => undefined);
    const unlistenDeepLinks = onOpenUrl((urls) => {
      void revealMainWindow();
      for (const url of urls) handleDeepLink(url);
    });

    return () => {
      unsubSession();
      unsubRecent();
      window.removeEventListener("popstate", rememberPath);
      window.removeEventListener("pushState", rememberPath);
      window.removeEventListener("replaceState", rememberPath);
      void unlistenDeepLinks.then((unlisten) => unlisten()).catch(() => undefined);
    };
  }, []);

  return null;
}
